import React from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { bodyFont, headingFont, ink, surface } from './tokens';

interface PanelProps {
  title: string;
  /** Sits under the title — say what the figure means, not that it is a chart. */
  subtitle?: string;
  /** Explanation shown on the info affordance. Omit to hide it. */
  hint?: string;
  /** Filters, legends or export controls, right-aligned in the header. */
  action?: React.ReactNode;
  /** Legend chips, laid out under the header and above the body. */
  legend?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The card every chart and table on the analytics page sits in. Replaces the
 * old `<Paper elevation={3}>` + `<Typography variant="h4">` pairing, which gave
 * every chart a heading the same size as the page title.
 */
export const Panel: React.FC<PanelProps> = ({
  title,
  subtitle,
  hint,
  action,
  legend,
  children
}) => (
  <Box
    sx={{
      backgroundColor: surface.card,
      border: `1px solid ${surface.border}`,
      borderRadius: '16px',
      p: { xs: '18px', md: '22px 24px' },
      display: 'flex',
      flexDirection: 'column',
      gap: '18px',
      height: '100%',
      minWidth: 0
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <Typography
            component='h2'
            sx={{
              fontFamily: headingFont,
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: '-.01em',
              color: ink.strong
            }}
          >
            {title}
          </Typography>
          {hint && (
            <Tooltip title={hint} placement='top' arrow enterTouchDelay={0}>
              <ButtonBase
                aria-label={`About ${title}`}
                disableRipple
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  color: ink.faint,
                  '&:hover': { color: ink.body }
                }}
              >
                <InfoOutlined sx={{ fontSize: 16 }} />
              </ButtonBase>
            </Tooltip>
          )}
        </Box>
        {subtitle && (
          <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.muted }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>

    {legend}

    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

/** One entry in a chart's legend: a colour swatch plus its series name. */
export const LegendSwatch: React.FC<{ color: string; label: string; value?: string }> = ({
  color,
  label,
  value
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
    <Box
      sx={{ width: 9, height: 9, borderRadius: '3px', backgroundColor: color, flex: 'none' }}
    />
    <Typography noWrap sx={{ fontFamily: bodyFont, fontSize: 13, color: ink.muted }}>
      {label}
    </Typography>
    {value !== undefined && (
      <Typography sx={{ fontFamily: bodyFont, fontWeight: 600, fontSize: 13, color: ink.strong }}>
        {value}
      </Typography>
    )}
  </Box>
);

export const LegendRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
    {children}
  </Box>
);

/**
 * Shown in place of a chart or table body when the selected range holds no
 * events. Keeps the panel's height so the page does not reflow as the period
 * changes.
 */
export const EmptyState: React.FC<{ message: string; height?: number }> = ({
  message,
  height = 300
}) => (
  <Box
    sx={{
      height,
      borderRadius: '12px',
      backgroundColor: surface.sunken,
      border: `1px dashed ${surface.borderStrong}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: '24px',
      textAlign: 'center'
    }}
  >
    <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.muted, maxWidth: 320 }}>
      {message}
    </Typography>
  </Box>
);

/** Shared shell for the floating tooltips recharts renders on hover. */
export const ChartTooltipCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children
}) => (
  <Box
    sx={{
      backgroundColor: surface.card,
      border: `1px solid ${surface.border}`,
      borderRadius: '10px',
      boxShadow: '0 12px 28px -14px rgba(23,34,44,.35)',
      p: '10px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      minWidth: 150
    }}
  >
    <Typography
      sx={{ fontFamily: bodyFont, fontWeight: 600, fontSize: 13, color: ink.strong }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

export const ChartTooltipRow: React.FC<{ color?: string; label: string; value: string }> = ({
  color,
  label,
  value
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    {color && (
      <Box
        sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: color, flex: 'none' }}
      />
    )}
    <Typography sx={{ fontFamily: bodyFont, fontSize: 13, color: ink.muted, flex: 1 }}>
      {label}
    </Typography>
    <Typography
      sx={{ fontFamily: bodyFont, fontWeight: 600, fontSize: 13, color: ink.strong }}
    >
      {value}
    </Typography>
  </Box>
);
